'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, ChevronLeft, ChevronRight, Home, Languages, ArrowLeftRight, BookOpen } from 'lucide-react';
import { useSession } from 'next-auth/react';
import WordByWordCaption from './WordByWordCaption';
import LetterHighlightedText from '../LetterHighlightedText';
import BackgroundParticles from '../BackgroundParticles';
import Asset3DRenderer from '../../3d-assets/Asset3DRenderer';
import MagicalEffect from '../MagicalEffect';
import PageAtmosphere from '../PageAtmosphere';
import VisualWeatherEffect from '../VisualWeatherEffect';
import EpicMomentEffect from '../EpicMomentEffect';
import MobileStoryEndModal from './MobileStoryEndModal';
import AudioBookProgressModal from '../audio-book-progress-modal';
import SubscriptionGate from '../SubscriptionGate';
import ProUpsellModal from '../ProUpsellModal';
import { useRouter } from 'next/navigation';

interface WordTiming {
  w: string;
  start: number;
  end: number;
}

interface PageData {
  pageNum: number;
  imageUrl: string;
  text: string;
  translatedText?: string;
  audioUrl: string;
  translatedAudioUrl?: string;
  soundEffectUrl?: string;
  duration: number;
  wordTimings?: WordTiming[];
  wordTimingsAlt?: WordTiming[];
  translatedWordTimings?: WordTiming[]; // For target language word highlighting
  bgmUrl?: string;
  bgmVolume?: number;
  asset3D?: {
    assetId: string;
    glbUrl: string;
    usdzUrl?: string;
    posterUrl?: string;
    placement: 'center';
    motion: 'auto-rotate' | 'none';
    sizeHint: 'featured';
    note?: string;
  };
  magicalEffect?: {
    effectId: string;
    note?: string;
  };
  atmosphere?: {
    gradient: [string, string];
    mood: string;
    timeOfDay?: string;
    brightness: 'light' | 'medium' | 'dark';
  };
  visualEffect?: {
    type: 'rain' | 'snow' | 'sunshine' | 'lightning' | 'fog' | 'wind' | 'none';
    intensity: 'light' | 'medium' | 'heavy';
  };
  epicMoment?: {
    enabled: boolean;
    effect: 'burst' | 'cascade' | 'ripple' | 'fireworks' | 'lightning-strike';
    trigger: 'auto' | 'on-page-load';
  };
  emotion?: string;
  objectSfxUrl?: string;
  // FIXED: Learning mode fields (was missing!)
  letterTimings?: Array<{ letter: string; start: number; end: number; wordIndex: number }>;
  translatedLetterTimings?: Array<{ letter: string; start: number; end: number; wordIndex: number }>;
}

interface AudioBookData {
  id: string;
  title: string;
  coverImageUrl: string | null;
  pagesData?: PageData[];
  pages?: PageData[];
  totalDuration: number;
  watermark?: boolean;
  status: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  language?: string;
  bilingualMode?: boolean;
  isTranslation?: boolean;
  montessoriMode?: boolean;
  isLearningContent?: boolean;
}

interface AudioBookMobilePlayerProps {
  audioBookId: string;
  isBilingual?: boolean;
}

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  nl: 'Dutch',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Mandarin',
  ar: 'Arabic',
  pl: 'Polish',
  lt: 'Lithuanian',
};

export default function AudioBookMobilePlayer({ audioBookId, isBilingual = false }: AudioBookMobilePlayerProps) {
  const router = useRouter();
  const { data: session } = useSession();
  const [bookData, setBookData] = useState<AudioBookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState<'source' | 'target'>('source');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; delay: number }>>([]);
  const [showStoryEndModal, setShowStoryEndModal] = useState(false);
  const [generatingBookId, setGeneratingBookId] = useState<string | null>(null);
  const [generatingTitle, setGeneratingTitle] = useState<string | null>(null);
  
  // NEW: Learning mode state
  const [isLearningMode, setIsLearningMode] = useState(false);
  const [showSpellingMode, setShowSpellingMode] = useState(false);
  
  // NEW: Subscription & access control state
  const [showSubscriptionGate, setShowSubscriptionGate] = useState(false);
  const [showProUpsellModal, setShowProUpsellModal] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const bgmAudioRef = useRef<HTMLAudioElement>(null);
  const controlHideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const shouldPlayAfterTransition = useRef(false);
  const firstAssetPageIndex = useRef<number | null>(null);
  
  // Touch gesture state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [lastTapTime, setLastTapTime] = useState<number>(0);
  const [showDoubleTapFeedback, setShowDoubleTapFeedback] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  // 🔧 CRITICAL FIX: Restore state from viewport router on mount
  useEffect(() => {
    const handleStateRestore = (event: CustomEvent) => {
      const state = event.detail;
      console.log('[Mobile Player] 📥 Restoring state from viewport change:', state);
      
      if (state.currentPageIndex !== undefined) {
        setCurrentPageIndex(state.currentPageIndex);
      }
      if (state.hasStarted !== undefined) {
        setHasStarted(state.hasStarted);
      }
      if (state.currentTime !== undefined && audioRef.current) {
        audioRef.current.currentTime = state.currentTime;
        setCurrentTime(state.currentTime);
      }
      if (state.isPlaying !== undefined && audioRef.current) {
        // Wait for audio to load before trying to play
        if (state.isPlaying) {
          setTimeout(() => {
            audioRef.current?.play().catch(e => 
              console.warn('[Mobile Player] Could not auto-play after restore:', e)
            );
          }, 300);
        }
      }
    };
    
    window.addEventListener('audiobook-state-restore' as any, handleStateRestore);
    
    return () => {
      window.removeEventListener('audiobook-state-restore' as any, handleStateRestore);
    };
  }, []);
  
  // 🔧 CRITICAL FIX: Broadcast state changes for preservation across viewport changes
  useEffect(() => {
    if (!bookData) return; // Only broadcast after book is loaded
    
    // Get current page for audio URL
    const pages = bookData?.pagesData || bookData?.pages || [];
    const currentPage = pages[currentPageIndex];
    
    // Determine current audio URL based on language
    const audioSrc = isBilingual && activeLanguage === 'target' && currentPage?.translatedAudioUrl
      ? currentPage.translatedAudioUrl
      : currentPage?.audioUrl || null;
    
    const state = {
      currentPageIndex,
      isPlaying,
      currentTime,
      hasStarted,
      audioSrc
    };
    
    // Dispatch state update event
    window.dispatchEvent(new CustomEvent('audiobook-state-update', {
      detail: state
    }));
  }, [currentPageIndex, isPlaying, currentTime, hasStarted, bookData, isBilingual, activeLanguage]);

  // Fetch book data and initialize particles
  useEffect(() => {
    fetchBookData();
    checkAccessControl();
    
    // Initialize particles (15 for mobile optimization)
    const newParticles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 6
    }));
    setParticles(newParticles);
  }, [audioBookId]);

  const fetchBookData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/audio-book/${audioBookId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch audio book');
      }
      
      const data = await response.json();
      console.log('[Mobile Player] Fetched book data:', data);
      
      // Determine highlighting mode based on spellingMode field
      // Check if book has explicit spellingMode setting (word/letter/none)
      // For learning content: respect the spellingMode field
      const spellingMode = data.spellingMode || 'word'; // Default to word mode
      
      if (data.isLearningContent === true && spellingMode === 'letter') {
        setIsLearningMode(true);
        setShowSpellingMode(true); // Enable letter-by-letter spelling
        console.log('[Mobile Player] 🎓 Learning mode: LETTER spelling - Letter-by-letter active');
      } else if (data.isLearningContent === true && spellingMode === 'word') {
        setIsLearningMode(true); // Still learning content but use word mode
        setShowSpellingMode(false); // Use word-by-word highlighting
        console.log('[Mobile Player] 🎓 Learning mode: WORD spelling - Word-by-word highlighting active');
      } else {
        // IMPORTANT: Reset learning mode for non-learning books
        setIsLearningMode(false);
        setShowSpellingMode(false);
        console.log('[Mobile Player] 📖 Regular bilingual mode - Word highlighting active');
      }
      
      setBookData(data);
      
      // Track first 3D asset page for special handling (reset for each new book)
      const pages = data?.pagesData || data?.pages || [];
      const firstAssetIndex = pages.findIndex((p: PageData) => p.asset3D);
      firstAssetPageIndex.current = firstAssetIndex !== -1 ? firstAssetIndex : null;
      if (firstAssetIndex !== -1) {
        console.log('[Mobile Player] 🎨 First 3D asset found on page:', firstAssetIndex);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('[Mobile Player] Error fetching book:', error);
      setLoading(false);
    }
  };

  const checkAccessControl = async () => {
    try {
      // Fetch first 3 featured samples
      const samplesRes = await fetch('/api/audio-book/samples');
      const samplesData = await samplesRes.json();
      const freeSampleIds = (samplesData.audioBooks || []).map((b: any) => b.id);
      
      // Fetch book data
      const bookRes = await fetch(`/api/audio-book/${audioBookId}`);
      if (!bookRes.ok) {
        setShowSubscriptionGate(true);
        setLoading(false);
        return;
      }
      const book = await bookRes.json();
      
      // Fetch user account
      const accountRes = await fetch('/api/account');
      const accountData = accountRes.ok ? await accountRes.json() : null;
      
      const isBookOwner = accountData && book.userId === accountData.id;
      const isInFreeBooks = freeSampleIds.includes(audioBookId);
      const hasSubscription = accountData?.isSubscriber || 
                              accountData?.subscriptionPlan === 'AMAYA_PLUS' || 
                              accountData?.subscriptionPlan === 'AMAYA_PRO';
      
      setIsOwner(isBookOwner);
      setIsPro(accountData?.subscriptionPlan === 'AMAYA_PRO');
      setIsSubscriber(hasSubscription);
      
      // Access: subscribers OR owners OR featured samples
      const hasAccess = hasSubscription || isBookOwner || isInFreeBooks;
      
      setCanAccess(hasAccess);
      if (!hasAccess) {
        setShowSubscriptionGate(true);
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setShowSubscriptionGate(true);
    }
  };

  // 🎯 PERFORMANCE: Predictive preload system for 3D assets (N+1 & N+2)
  useEffect(() => {
    if (!bookData?.pagesData) return;
    
    // Preload next 2 pages' 3D assets for instant load
    const upcomingPages = bookData.pagesData.slice(
      currentPageIndex + 1,
      currentPageIndex + 3
    );
    
    upcomingPages.forEach((page: PageData) => {
      if (page.asset3D?.glbUrl) {
        // ✅ CRITICAL FIX: Use fetch with CORS mode instead of <link> prefetch
        // Link prefetch doesn't properly handle CORS for cross-origin resources
        fetch(page.asset3D.glbUrl, { 
          mode: 'cors',
          credentials: 'omit',
          priority: 'low' as any // Hint: low priority background fetch
        }).then(response => {
          if (response.ok) {
            console.log('[Preload] ✅ 3D asset preloaded:', page.asset3D!.assetId);
          }
        }).catch(err => {
          console.log('[Preload] ⚠️  Preload failed (not critical):', page.asset3D!.assetId);
        });
        
        // Preload USDZ for AR (iOS)
        if (page.asset3D.usdzUrl) {
          fetch(page.asset3D.usdzUrl, {
            mode: 'cors',
            credentials: 'omit',
            priority: 'low' as any
          }).catch(() => {}); // Silent fail
        }
      }
    });
    
    // Cleanup handled automatically by browser
  }, [bookData, currentPageIndex]);

  // Check if learning content requires Pro subscription
  useEffect(() => {
    if (bookData?.isLearningContent && !isPro && !isOwner) {
      setShowProUpsellModal(true);
    } else {
      setShowProUpsellModal(false); // FIXED: Close modal if user IS Pro or owner
    }
  }, [bookData, isPro, isOwner]);

  const pages = bookData?.pagesData || bookData?.pages || [];
  const currentPage = pages[currentPageIndex];

  // Get current audio URL and word timings based on active language
  const currentAudioUrl = isBilingual && activeLanguage === 'target' && currentPage?.translatedAudioUrl
    ? currentPage.translatedAudioUrl
    : currentPage?.audioUrl || '';

  const currentWordTimings = isBilingual && activeLanguage === 'target' && (currentPage?.translatedWordTimings || currentPage?.wordTimingsAlt)
    ? (currentPage.translatedWordTimings || currentPage.wordTimingsAlt)
    : currentPage?.wordTimings || [];

  // Get current text based on active language
  const currentAudioText = isBilingual && activeLanguage === 'target' && currentPage?.translatedText
    ? currentPage.translatedText
    : currentPage?.text || '';

  // Debug logging for word timings
  useEffect(() => {
    console.log('[Mobile Player] 📝 Page', currentPageIndex, '- wordTimings count:', currentWordTimings.length);
    if (currentWordTimings.length > 0) {
      console.log('[Mobile Player] First word:', currentWordTimings[0].w, 'at', currentWordTimings[0].start, 's');
      console.log('[Mobile Player] Last word:', currentWordTimings[currentWordTimings.length - 1].w, 'at', currentWordTimings[currentWordTimings.length - 1].end, 's');
    }
  }, [currentPageIndex, currentWordTimings]);

  // Auto-advance handler - defined BEFORE useEffect to avoid stale closures
  const handleAutoAdvance = useCallback(() => {
    console.log('[Mobile Player] 🔄 handleAutoAdvance called - currentPage:', currentPageIndex, 'totalPages:', pages.length);
    if (currentPageIndex < pages.length - 1) {
      console.log('[Mobile Player] ➡️ Auto-advancing to page', currentPageIndex + 1);
      shouldPlayAfterTransition.current = true;
      setIsTransitioning(true);
      const wasPlaying = true; // We're in handleEnded, so yes we were playing

      if (audioRef.current) {
        audioRef.current.pause();
      }

      setTimeout(() => {
        console.log('[Mobile Player] Executing auto-advance - moving to page', currentPageIndex + 1);
        setCurrentPageIndex(prev => prev + 1);
        setCurrentTime(0);
        setIsTransitioning(false);
        shouldPlayAfterTransition.current = true;
      }, 600);
    } else {
      console.log('[Mobile Player] ✅ Story completed - last page reached');
      // Fade out BGM when story ends
      if (bgmAudioRef.current && !bgmAudioRef.current.paused) {
        const bgm = bgmAudioRef.current;
        const fadeOutInterval = setInterval(() => {
          if (bgm.volume > 0.02) {
            bgm.volume = Math.max(0, bgm.volume - 0.04);
          } else {
            bgm.pause();
            bgm.volume = 0;
            clearInterval(fadeOutInterval);
          }
        }, 50);
      }
      setShowStoryEndModal(true);
    }
  }, [currentPageIndex, pages.length]);

  // Audio state management - 6 listeners for robust state sync
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      console.log('[Mobile Player] ⚠️ No audio element in useEffect');
      return;
    }

    console.log('[Mobile Player] 🔗 Attaching event listeners to audio element');

    const syncPlayState = () => {
      const playing = !audio.paused && !audio.ended;
      console.log('[Mobile Player] syncPlayState - playing:', playing, 'paused:', audio.paused, 'ended:', audio.ended);
      setIsPlaying(playing);
    };

    const handleEnded = () => {
      console.log('[Mobile Player] ⏹️ Audio ended on page', currentPageIndex);
      setIsPlaying(false);
      handleAutoAdvance();
    };

    const handleError = (e: Event) => {
      console.error('[Mobile Player] ❌ Audio error:', e);
      setIsPlaying(false);
    };

    const handleTimeUpdate = () => {
      // Only log occasionally to avoid spam
      if (Math.floor(audio.currentTime * 10) % 10 === 0) {
        console.log('[Mobile Player] ⏱️ Time update:', audio.currentTime.toFixed(2), 's on page', currentPageIndex);
      }
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      console.log('[Mobile Player] 📊 Loaded metadata - duration:', audio.duration, 's on page', currentPageIndex);
      setDuration(audio.duration);
    };

    // Add all 6 event listeners
    audio.addEventListener('play', syncPlayState);
    audio.addEventListener('pause', syncPlayState);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', syncPlayState);
    audio.addEventListener('canplay', syncPlayState);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    console.log('[Mobile Player] ✅ Event listeners attached');

    return () => {
      console.log('[Mobile Player] 🔓 Removing event listeners');
      audio.removeEventListener('play', syncPlayState);
      audio.removeEventListener('pause', syncPlayState);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', syncPlayState);
      audio.removeEventListener('canplay', syncPlayState);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [currentPageIndex, handleAutoAdvance]);

  // 🔊 iOS-CRITICAL: Ensure narration audio is always at 100% volume on mobile
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    // Force narration to 100% volume for clear voice on mobile devices
    audio.volume = 1.0;
    console.log('[Mobile Player] 🔊 Narration volume set to 100%');
    
    // 🍎 iOS FIX: Re-enforce narration volume after audio loads
    const enforceNarrationVolume = () => {
      audio.volume = 1.0;
      console.log('[Mobile Player] 🍎 iOS: Enforced narration volume to 100%');
    };
    
    audio.addEventListener('loadeddata', enforceNarrationVolume);
    audio.addEventListener('canplay', enforceNarrationVolume);
    audio.addEventListener('play', enforceNarrationVolume);
    
    return () => {
      audio.removeEventListener('loadeddata', enforceNarrationVolume);
      audio.removeEventListener('canplay', enforceNarrationVolume);
      audio.removeEventListener('play', enforceNarrationVolume);
    };
  }, [currentAudioUrl]);

  // 🍎 iOS-CRITICAL: BGM handling with proper iOS audio load management
  useEffect(() => {
    const bgmAudio = bgmAudioRef.current;
    if (!bgmAudio || !currentPage?.bgmUrl) return;

    // Calculate target volume (capped at 10% for mobile)
    const pageVolume = currentPage.bgmVolume || 0.10;
    const targetVolume = Math.min(pageVolume, 0.10); // Never exceed 10% on mobile
    
    console.log('[Mobile Player] 🎵 Setting up BGM:', currentPage.bgmUrl);
    console.log('[Mobile Player] 🎵 Target volume:', targetVolume, '(capped at 10%)');

    // Set volume BEFORE loading (for browsers that support it)
    bgmAudio.volume = targetVolume;
    bgmAudio.loop = true;
    bgmAudio.src = currentPage.bgmUrl;
    
    // 🍎 CRITICAL iOS FIX: Re-apply volume AFTER audio loads
    // iOS Safari resets volume during load, so we must set it again after 'canplaythrough'
    const enforceVolume = () => {
      console.log('[Mobile Player] 🍎 iOS: Enforcing BGM volume after load:', targetVolume);
      bgmAudio.volume = targetVolume;
      
      // Double-check volume was actually set (iOS Safari bug workaround)
      setTimeout(() => {
        if (Math.abs(bgmAudio.volume - targetVolume) > 0.01) {
          console.warn('[Mobile Player] ⚠️ iOS: Volume not set correctly, retrying...');
          bgmAudio.volume = targetVolume;
        }
        console.log('[Mobile Player] ✅ Final BGM volume:', bgmAudio.volume);
      }, 100);
    };
    
    // 🍎 iOS: Enforce volume on multiple load events (belt and suspenders approach)
    bgmAudio.addEventListener('loadeddata', enforceVolume);
    bgmAudio.addEventListener('canplay', enforceVolume);
    bgmAudio.addEventListener('canplaythrough', enforceVolume);
    
    // Also enforce volume right before playing (iOS last resort)
    const enforceVolumeBeforePlay = () => {
      console.log('[Mobile Player] 🍎 iOS: Enforcing volume before play');
      bgmAudio.volume = targetVolume;
    };
    bgmAudio.addEventListener('play', enforceVolumeBeforePlay, { once: false });
    
    // Start loading
    bgmAudio.load();

    if (isPlaying) {
      bgmAudio.play().catch(err => console.log('[Mobile Player] BGM autoplay prevented:', err));
    }

    return () => {
      // Cleanup event listeners
      bgmAudio.removeEventListener('loadeddata', enforceVolume);
      bgmAudio.removeEventListener('canplay', enforceVolume);
      bgmAudio.removeEventListener('canplaythrough', enforceVolume);
      bgmAudio.removeEventListener('play', enforceVolumeBeforePlay);
      bgmAudio.pause();
    };
  }, [currentPage?.bgmUrl, currentPageIndex, isPlaying]);

  // Sync BGM with playback + iOS volume enforcement
  useEffect(() => {
    const bgmAudio = bgmAudioRef.current;
    if (!bgmAudio) return;

    if (isPlaying && currentPage?.bgmUrl) {
      // 🍎 iOS: Defensive volume check before playing
      const pageVolume = currentPage.bgmVolume || 0.10;
      const targetVolume = Math.min(pageVolume, 0.10);
      
      if (Math.abs(bgmAudio.volume - targetVolume) > 0.01) {
        console.log('[Mobile Player] 🍎 iOS: Correcting BGM volume before play:', bgmAudio.volume, '→', targetVolume);
        bgmAudio.volume = targetVolume;
      }
      
      bgmAudio.play().catch(err => console.log('[Mobile Player] BGM play prevented:', err));
    } else {
      bgmAudio.pause();
    }
  }, [isPlaying, currentPage]);

  // Auto-hide controls and header after 3 seconds when playing
  useEffect(() => {
    if (!controlsVisible) return;

    if (controlHideTimerRef.current) {
      clearTimeout(controlHideTimerRef.current);
    }

    // Auto-hide when playing
    if (isPlaying) {
      controlHideTimerRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    }

    return () => {
      if (controlHideTimerRef.current) {
        clearTimeout(controlHideTimerRef.current);
      }
    };
  }, [controlsVisible, isPlaying]);

  // Show controls when INTENTIONALLY paused (not during page transitions)
  useEffect(() => {
    // Only show controls if audio is paused AND we're not transitioning between pages
    // This preserves the user's control visibility preference during page changes
    if (!isPlaying && !isTransitioning && hasStarted) {
      // User intentionally paused - show controls
      setControlsVisible(true);
    }
  }, [isPlaying, isTransitioning, hasStarted]);

  // Keyboard support: Spacebar to play/pause
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isTransitioning) {
        e.preventDefault();
        handlePlayPause();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying, isTransitioning]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => console.error('[Mobile Player] Play error:', err));
    }
  };

  const handleBeginAdventure = () => {
    console.log('[Mobile Player] 🎬 Beginning adventure - starting playback...');
    
    // Reset audio state to beginning
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      setCurrentTime(0);
      console.log('[Mobile Player] Reset audio to beginning - currentTime: 0');
    }
    
    setHasStarted(true);
    setControlsVisible(true);
    
    // Small delay to ensure overlay fades and state updates, then start playing
    setTimeout(() => {
      if (audio && currentAudioUrl) {
        console.log('[Mobile Player] Starting playback on cover page...');
        console.log('[Mobile Player] Audio ready state:', audio.readyState);
        console.log('[Mobile Player] Current time before play:', audio.currentTime);
        
        audio.play()
          .then(() => {
            console.log('[Mobile Player] ✅ Cover page audio playing');
            console.log('[Mobile Player] Current time after play:', audio.currentTime);
            console.log('[Mobile Player] Is playing:', !audio.paused);
            
            // Force a time update to sync caption immediately
            setCurrentTime(audio.currentTime);
          })
          .catch(err => {
            console.error('[Mobile Player] Autoplay prevented:', err);
          });
      }
    }, 300);
  };

  const handlePreviousPage = () => {
    if (currentPageIndex > 0 && !isTransitioning) {
      setIsTransitioning(true);
      const wasPlaying = isPlaying;
      
      if (audioRef.current) {
        audioRef.current.pause();
      }

      setTimeout(() => {
        setCurrentPageIndex(prev => prev - 1);
        setCurrentTime(0);
        setIsTransitioning(false);
        
        if (wasPlaying) {
          shouldPlayAfterTransition.current = true;
        }
      }, 600);
    }
  };

  const handleNextPage = () => {
    console.log('[Mobile Player] 📄 handleNextPage called - currentPage:', currentPageIndex, 'isTransitioning:', isTransitioning);
    if (currentPageIndex < pages.length - 1 && !isTransitioning) {
      console.log('[Mobile Player] Starting page transition to page', currentPageIndex + 1);
      setIsTransitioning(true);
      const wasPlaying = isPlaying || shouldPlayAfterTransition.current;
      console.log('[Mobile Player] wasPlaying:', wasPlaying, 'isPlaying:', isPlaying, 'shouldPlayAfterTransition:', shouldPlayAfterTransition.current);
      
      if (audioRef.current) {
        audioRef.current.pause();
      }

      setTimeout(() => {
        console.log('[Mobile Player] Executing page transition - moving to page', currentPageIndex + 1);
        setCurrentPageIndex(prev => prev + 1);
        setCurrentTime(0);
        setIsTransitioning(false);
        
        if (wasPlaying) {
          shouldPlayAfterTransition.current = true;
          console.log('[Mobile Player] Will auto-play after transition');
        }
      }, 600);
    } else if (isTransitioning) {
      console.log('[Mobile Player] ⏸️ Skipping handleNextPage - already transitioning');
    }
  };

  // Play audio after page transition (like desktop)
  useEffect(() => {
    if (!isTransitioning && shouldPlayAfterTransition.current) {
      console.log('[Mobile Player] Auto-play after transition...');
      setTimeout(() => {
        const audio = audioRef.current;
        if (audio && currentAudioUrl) {
          audio.play()
            .then(() => {
              shouldPlayAfterTransition.current = false;
              console.log('[Mobile Player] ✅ Auto-play successful after transition');
            })
            .catch(err => {
              console.error('[Mobile Player] Auto-play error:', err);
              shouldPlayAfterTransition.current = false;
            });
        }
      }, 100);
    }
  }, [isTransitioning, currentPageIndex, currentAudioUrl]);

  const handleLanguageToggle = () => {
    const wasPlaying = isPlaying;
    const currentTimeStamp = currentTime;
    
    if (audioRef.current) {
      audioRef.current.pause();
    }

    setActiveLanguage(prev => prev === 'source' ? 'target' : 'source');

    // Resume playback after language switch
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.currentTime = currentTimeStamp;
        if (wasPlaying) {
          audioRef.current.play().catch(err => console.error('[Mobile Player] Resume error:', err));
        }
      }
    }, 100);
  };

  const handleRepeatStory = () => {
    console.log('[Mobile Player] 🔁 Repeating story - reset to beginning');
    setCurrentPageIndex(0);
    setCurrentTime(0);
    setIsPlaying(false);
    setHasStarted(true); // Keep story started state
    shouldPlayAfterTransition.current = false;
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
    
    // Reset BGM volume
    if (bgmAudioRef.current) {
      bgmAudioRef.current.volume = 0.10;
    }
  };

  const handleAutoPlayNext = (bookId: string) => {
    console.log('[Mobile Player] ⏭️ Auto-playing next book:', bookId);
    // Navigation handled by modal - just log for now
  };

  const handleStartGeneration = (bookId: string, title: string) => {
    console.log('[Mobile Player] 🎨 Starting generation:', bookId, title);
    setGeneratingBookId(bookId);
    setGeneratingTitle(title);
  };

  // Touch gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe && currentPageIndex < pages.length - 1) {
      handleNextPage();
    }
    if (isRightSwipe && currentPageIndex > 0) {
      handlePreviousPage();
    }
  };

  const handleImageTap = (e: React.TouchEvent | React.MouseEvent) => {
    const currentTime = Date.now();
    const timeSinceLastTap = currentTime - lastTapTime;
    
    // Double tap detection (within 300ms)
    if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
      // Double tap: Play/Pause (like video players)
      handlePlayPause();
      
      // Show visual feedback
      setShowDoubleTapFeedback(true);
      setTimeout(() => setShowDoubleTapFeedback(false), 500);
      
      setLastTapTime(0); // Reset to prevent triple tap
    } else {
      // Single tap: Toggle controls visibility
      setControlsVisible(prev => !prev);
      setLastTapTime(currentTime);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="mobile-player-loading">
        {/* Animated Background */}
        <div className="loading-bg-gradient" />
        
        {/* Floating Particles */}
        <div className="loading-particles">
          {[...Array(15)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${Math.random() * 10 + 15}s`,
            }} />
          ))}
        </div>

        {/* Main Loading Content */}
        <div className="loading-content">
          {/* Animated Rings */}
          <div className="loading-icon-container">
            <div className="glow-ring outer-ring" />
            <div className="glow-ring inner-ring" />
            
            {/* Glass Icon Container */}
            <div className="glass-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
          </div>

          {/* Loading Text */}
          <div className="loading-text-container">
            <h2 className="loading-title">Loading Story</h2>
            <div className="loading-dots">
              <div className="dot" style={{ animationDelay: '0s' }} />
              <div className="dot" style={{ animationDelay: '0.2s' }} />
              <div className="dot" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        </div>

        <style jsx>{`
          .mobile-player-loading {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
            overflow: hidden;
          }

          .loading-bg-gradient {
            position: absolute;
            inset: 0;
            background: radial-gradient(circle at 30% 50%, rgba(102, 126, 234, 0.2) 0%, transparent 50%),
                        radial-gradient(circle at 70% 50%, rgba(118, 75, 162, 0.2) 0%, transparent 50%);
            animation: pulseGlow 8s ease-in-out infinite;
          }

          .loading-particles {
            position: absolute;
            inset: 0;
            pointer-events: none;
          }

          .particle {
            position: absolute;
            width: 3px;
            height: 3px;
            background: rgba(255, 255, 255, 0.4);
            borderRadius: 50%;
            boxShadow: 0 0 8px rgba(255, 255, 255, 0.6);
            animation: floatUp linear infinite;
          }

          @keyframes floatUp {
            0% {
              transform: translateY(0) translateX(0);
              opacity: 0;
            }
            10% {
              opacity: 0.6;
            }
            90% {
              opacity: 0.3;
            }
            100% {
              transform: translateY(-100vh) translateX(${Math.random() * 50 - 25}px);
              opacity: 0;
            }
          }

          .loading-content {
            position: relative;
            z-index: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2rem;
          }

          .loading-icon-container {
            position: relative;
            width: 100px;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .glow-ring {
            position: absolute;
            border-radius: 50%;
            animation: ringPulse 2s ease-in-out infinite;
          }

          .outer-ring {
            width: 100px;
            height: 100px;
            border: 2px solid rgba(102, 126, 234, 0.3);
          }

          .inner-ring {
            width: 75px;
            height: 75px;
            border: 2px solid rgba(118, 75, 162, 0.3);
            animation-delay: 0.5s;
          }

          @keyframes ringPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.3;
            }
            50% {
              transform: scale(1.15);
              opacity: 0.7;
            }
          }

          .glass-icon {
            position: relative;
            width: 56px;
            height: 56px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%);
            backdropFilter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            borderRadius: 18px;
            border: 2px solid rgba(255, 255, 255, 0.2);
            boxShadow: 0 15px 40px rgba(102, 126, 234, 0.3),
                        inset 0 1px 0 rgba(255, 255, 255, 0.3);
            animation: iconBounce 2s ease-in-out infinite;
          }

          @keyframes iconBounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          .loading-text-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.75rem;
          }

          .loading-title {
            font-size: 1.5rem;
            font-weight: 800;
            color: white;
            margin: 0;
            textShadow: 0 4px 20px rgba(0, 0, 0, 0.3);
            letterSpacing: 0.02em;
          }

          .loading-dots {
            display: flex;
            alignItems: center;
            justifyContent: center;
            gap: 0.5rem;
            height: 20px;
          }

          .dot {
            width: 8px;
            height: 8px;
            borderRadius: 50%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            boxShadow: 0 2px 8px rgba(102, 126, 234, 0.5);
            animation: dotPulse 1.5s ease-in-out infinite;
          }

          @keyframes dotPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.5;
            }
            50% {
              transform: scale(1.5);
              opacity: 1;
            }
          }

          @keyframes pulseGlow {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }
        `}</style>
      </div>
    );
  }

  if (!bookData || pages.length === 0) {
    return (
      <div className="mobile-player-error">
        <div>Book not found or no pages available</div>
        <style jsx>{`
          .mobile-player-error {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            padding: 2rem;
            text-align: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
        `}</style>
      </div>
    );
  }

  const sourceLangName = LANGUAGE_NAMES[bookData.sourceLanguage || bookData.language || 'en'] || 'English';
  const targetLangName = LANGUAGE_NAMES[bookData.targetLanguage || 'es'] || 'Spanish';

  return (
    <div className="mobile-player">
      {/* Background particles with reduced density */}
      <BackgroundParticles particles={particles} />

      {/* Audio elements */}
      <audio ref={audioRef} src={currentAudioUrl} preload="auto" />
      <audio ref={bgmAudioRef} preload="auto" />

      {/* Top: Full-bleed image section */}
      <div 
        className="image-section"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleImageTap}
      >
        {/* Page image */}
        <img 
          src={currentPage?.imageUrl} 
          alt={`Page ${currentPageIndex + 1}`}
          className={`page-image ${isTransitioning ? 'transitioning' : ''}`}
        />

        {/* Page Atmosphere - Color overlay on top of image */}
        {currentPage?.atmosphere && (
          <PageAtmosphere
            gradient={currentPage.atmosphere.gradient}
            mood={currentPage.atmosphere.mood}
            timeOfDay={currentPage.atmosphere.timeOfDay}
            brightness={currentPage.atmosphere.brightness}
          />
        )}

        {/* Featured 3D Asset - Interactive objects */}
        {currentPage?.asset3D && (
          <Asset3DRenderer
            key={`3d-mobile-${currentPageIndex}-${currentPage.asset3D.assetId}`}
            glbUrl={currentPage.asset3D.glbUrl}
            usdzUrl={currentPage.asset3D.usdzUrl}
            assetId={currentPage.asset3D.assetId}
            placement="center"
            montessoriMode={bookData?.montessoriMode}
            isPlaying={isPlaying}
            objectSfxUrl={currentPage.objectSfxUrl}
            isFirstAssetPage={currentPageIndex === firstAssetPageIndex.current}
          />
        )}

        {/* Visual Weather Effect */}
        {currentPage?.visualEffect && currentPage.visualEffect.type !== 'none' && (
          <VisualWeatherEffect
            type={currentPage.visualEffect.type}
            intensity={currentPage.visualEffect.intensity}
          />
        )}

        {/* Magical Effect */}
        {currentPage?.magicalEffect && (
          <MagicalEffect
            effectId={currentPage.magicalEffect.effectId}
          />
        )}

        {/* Epic Moment Effect */}
        {currentPage?.epicMoment?.enabled && (
          <EpicMomentEffect
            effect={currentPage.epicMoment.effect}
            trigger={currentPage.epicMoment.trigger}
          />
        )}

        {/* Double-tap visual feedback */}
        {showDoubleTapFeedback && (
          <div className="double-tap-feedback">
            {isPlaying ? <Pause size={80} /> : <Play size={80} />}
          </div>
        )}

        {/* Begin Adventure Overlay - Show on first page before starting */}
        {!hasStarted && currentPageIndex === 0 && (
          <div className="begin-adventure-overlay" onClick={(e) => e.stopPropagation()}>
            {/* Library Button - Top Left on Mobile */}
            <button
              onClick={() => {
                window.location.href = '/audio-book/library';
              }}
              className="mobile-library-button"
              aria-label="Go to library"
            >
              <Home size={20} />
              <span>Library</span>
            </button>

            <button 
              onClick={handleBeginAdventure}
              className="begin-adventure-button"
              aria-label={isLearningMode ? "Begin learning journey" : "Begin adventure"}
            >
              <Play size={40} fill="white" />
              <span className="begin-text">{isLearningMode ? 'Begin Learning' : 'Begin Adventure'}</span>
            </button>
          </div>
        )}

        {/* Header - only show when controls visible and story has started */}
        {controlsVisible && hasStarted && (
          <div className="mobile-header">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = '/audio-book/library';
              }}
              className="header-button"
              aria-label="Go home"
            >
              <Home size={20} />
            </button>
            <h1 className="header-title">{bookData.title}</h1>
            <div className="header-right-buttons">
              <div className="page-counter">
                {currentPageIndex + 1} / {pages.length}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Caption lane below image - always shown */}
      <div className="caption-section">
        {isLearningMode && showSpellingMode && bookData?.isLearningContent ? (
          // LEARNING MODE - Spelling with language flag (ONLY for books with letter timing data)
          <div style={{
            textAlign: 'center',
            padding: '1.5rem 1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center',
          }}>
            {/* Language Flag Badge */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              padding: '0.5rem 1.25rem',
              borderRadius: '12px',
              fontSize: '0.875rem',
              fontWeight: 800,
              marginBottom: '0.5rem',
              display: 'inline-block',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            }}>
              {activeLanguage === 'source' ? '🎯' : '🌍'} {
                LANGUAGE_NAMES[activeLanguage === 'source' ? (bookData.sourceLanguage || 'en') : (bookData.targetLanguage || 'es')]
              }
            </div>
            
            {/* Letter-by-Letter Spelling */}
            <LetterHighlightedText
              text={currentAudioText}
              letterTimings={
                isBilingual && activeLanguage === 'target' && currentPage?.translatedLetterTimings
                  ? currentPage.translatedLetterTimings
                  : currentPage?.letterTimings || []
              }
              currentTime={currentTime}
              isPlaying={isPlaying}
              activeLanguage={activeLanguage}
              showOnlySpelling={true}
            />
          </div>
        ) : (
          // FULL TEXT MODE - Complete sentences with word highlighting
          <WordByWordCaption
            wordTimings={currentWordTimings || []}
            currentTime={currentTime}
            isPlaying={isPlaying}
            isLastPage={currentPageIndex === pages.length - 1}
          />
        )}
      </div>

      {/* 🎮 Kid-Friendly Control Bar - Below caption text for award-winning UX */}
      {hasStarted && (
        <div className={`control-bar-section ${controlsVisible ? 'visible' : 'hidden'}`}>
          <div className="control-bar-container">
            {/* Previous button */}
            <button
              onClick={handlePreviousPage}
              disabled={currentPageIndex === 0 || isTransitioning}
              className="control-button nav-button"
              aria-label="Previous page"
            >
              <ChevronLeft size={32} strokeWidth={3} />
            </button>

            {/* Play/Pause button - Hero button */}
            <button
              onClick={handlePlayPause}
              className="control-button play-button"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={48} strokeWidth={3} /> : <Play size={48} strokeWidth={3} />}
            </button>

            {/* Next button */}
            <button
              onClick={handleNextPage}
              disabled={currentPageIndex === pages.length - 1 || isTransitioning}
              className="control-button nav-button"
              aria-label="Next page"
            >
              <ChevronRight size={32} strokeWidth={3} />
            </button>
          </div>
        </div>
      )}

      {/* Bilingual language toggle */}
      {isBilingual && (
        <div className="language-toggle-section" style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          <button 
            onClick={handleLanguageToggle}
            className="language-toggle-chip"
            aria-label="Toggle language"
          >
            <Languages size={16} />
            <span>
              {activeLanguage === 'source' ? sourceLangName : targetLangName}
            </span>
            <ArrowLeftRight size={14} />
          </button>
          
          {/* Spelling Mode Toggle - ONLY for learning books with letter timing data */}
          {isLearningMode && bookData?.isLearningContent && (
            <button
              onClick={() => setShowSpellingMode(!showSpellingMode)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                background: showSpellingMode 
                  ? 'linear-gradient(135deg, #10B981, #059669)'
                  : 'linear-gradient(135deg, #6B7280, #4B5563)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.8125rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease',
              }}
              aria-label={showSpellingMode ? 'Show full text' : 'Show spelling'}
            >
              {showSpellingMode ? (
                <>
                  <BookOpen size={16} />
                  <span>Full Text</span>
                </>
              ) : (
                <>
                  <Languages size={16} />
                  <span>Spelling</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Story End Modal - Mobile Optimized */}
      {showStoryEndModal && bookData && (
        <MobileStoryEndModal
          isOpen={showStoryEndModal}
          onClose={() => setShowStoryEndModal(false)}
          onRepeatStory={handleRepeatStory}
          originalBookId={audioBookId}
          currentBookId={audioBookId}
          storyTitle={bookData.title}
          currentLanguage={bookData.sourceLanguage || bookData.language || 'en'}
          showBilingualOption={!isBilingual}
          onAutoPlayNext={handleAutoPlayNext}
          onStartGeneration={handleStartGeneration}
        />
      )}

      {/* Generation Progress Modal */}
      <AudioBookProgressModal
        isGenerating={!!generatingBookId}
        audioBookId={generatingBookId}
      />

      {/* SUBSCRIPTION GATE - For non-subscribers/non-owners */}
      {showSubscriptionGate && (
        <SubscriptionGate
          isAuthenticated={!!session}
          currentPath={`/audio-book/${audioBookId}`}
        />
      )}

      {/* PRO UPSELL MODAL - For learning mode requiring Pro */}
      {showProUpsellModal && (
        <ProUpsellModal
          isOpen={showProUpsellModal}
          onClose={() => {
            setShowProUpsellModal(false);
            router.push('/pricing');
          }}
        />
      )}

      <style jsx>{`
        .mobile-player {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .image-section {
          position: relative;
          flex: 0 0 60vh;
          width: 100%;
          overflow: hidden;
          background: #000;
        }

        .page-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: opacity 600ms ease-in-out, transform 600ms ease-in-out;
        }

        .page-image.transitioning {
          opacity: 0;
          transform: scale(0.95);
        }

        .double-tap-feedback {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(10px);
          border-radius: 50%;
          width: 120px;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          animation: doubleTapFade 0.5s ease-out;
          pointer-events: none;
          z-index: 200;
        }

        @keyframes doubleTapFade {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.8);
          }
          20% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.1);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        .mobile-header {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem;
          background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%);
          z-index: 50;
          transition: opacity 200ms ease;
        }

        .header-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .header-button:active {
          transform: scale(0.95);
          background: rgba(255, 255, 255, 0.3);
        }

        .header-title {
          flex: 1;
          margin: 0 1rem;
          font-size: 1rem;
          font-weight: 600;
          color: white;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .header-right-buttons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .page-counter {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 600;
          min-width: 60px;
          text-align: right;
        }

        /* 🎨 Caption section - Now with no bottom controls overlay */
        .caption-section {
          flex: 0 0 auto;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 1rem 1rem 0.75rem 1rem;
          background: linear-gradient(180deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.98) 100%);
          min-height: 100px;
          max-height: 160px;
          overflow: hidden;
        }

        /* 🎮 Award-Winning Control Bar Section - Below caption for pristine artwork */
        .control-bar-section {
          flex: 0 0 auto;
          padding: 0.75rem 1rem 1rem 1rem;
          background: linear-gradient(180deg, rgba(22, 33, 62, 0.98) 0%, rgba(15, 52, 96, 1) 100%);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 300ms ease, transform 300ms ease;
        }

        .control-bar-section.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .control-bar-section.hidden {
          opacity: 0;
          transform: translateY(20px);
          pointer-events: none;
        }

        .control-bar-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          max-width: 400px;
          width: 100%;
        }

        /* 🎯 Kid-Friendly Control Buttons - Larger, colorful, award-winning */
        .control-button {
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          position: relative;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
        }

        .control-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          transform: scale(0.9);
        }

        .control-button:not(:disabled):active {
          transform: scale(0.88);
        }

        /* Previous/Next Navigation Buttons */
        .nav-button {
          min-width: 60px;
          min-height: 60px;
          background: linear-gradient(135deg, 
            rgba(102, 126, 234, 0.8) 0%, 
            rgba(118, 75, 162, 0.8) 100%);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .nav-button:not(:disabled):hover {
          background: linear-gradient(135deg, 
            rgba(102, 126, 234, 1) 0%, 
            rgba(118, 75, 162, 1) 100%);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
        }

        .nav-button:not(:disabled):active {
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.5);
        }

        /* Hero Play/Pause Button - Center of attention */
        .play-button {
          min-width: 80px;
          min-height: 80px;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.95) 0%, 
            rgba(255, 255, 255, 0.85) 100%);
          border: 3px solid rgba(102, 126, 234, 0.6);
          box-shadow: 
            0 10px 30px rgba(102, 126, 234, 0.4),
            0 0 0 0 rgba(102, 126, 234, 0.4);
          color: #667eea;
          animation: pulseGlow 2s ease-in-out infinite;
        }

        .play-button:active {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 1) 0%, 
            rgba(255, 255, 255, 0.95) 100%);
          box-shadow: 
            0 12px 35px rgba(102, 126, 234, 0.5),
            0 0 0 8px rgba(102, 126, 234, 0.2);
        }

        /* Pulse animation for play button */
        @keyframes pulseGlow {
          0%, 100% {
            box-shadow: 
              0 10px 30px rgba(102, 126, 234, 0.4),
              0 0 0 0 rgba(102, 126, 234, 0);
          }
          50% {
            box-shadow: 
              0 10px 30px rgba(102, 126, 234, 0.4),
              0 0 0 12px rgba(102, 126, 234, 0.1);
          }
        }

        .language-toggle-section {
          padding: 1rem;
          display: flex;
          justify-content: center;
          background: rgba(26, 26, 46, 0.95);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .language-toggle-chip {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 50px;
          color: white;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.3s ease;
          min-height: 44px;
        }

        .language-toggle-chip:active {
          transform: scale(0.95);
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.5) 0%, rgba(118, 75, 162, 0.5) 100%);
        }

        @media (prefers-reduced-motion: reduce) {
          .page-image,
          .glass-controls,
          .control-button,
          .language-toggle-chip {
            transition: none;
          }
        }

        /* 🎬 Begin Adventure Overlay - Simple button on first page */
        .begin-adventure-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.15); /* More transparent */
          backdrop-filter: blur(1px); /* Minimal blur */
          -webkit-backdrop-filter: blur(1px);
          z-index: 150;
          animation: overlayFadeIn 0.3s ease-out;
        }

        /* 🏆 2026 Award-Winning Library Button - Top Left */
        .mobile-library-button {
          position: absolute;
          top: 1rem;
          left: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.3) 0%, 
            rgba(255, 255, 255, 0.2) 100%);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-radius: 50px;
          color: white;
          font-size: 0.9375rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2),
                      inset 0 1px 0 rgba(255, 255, 255, 0.5);
          min-height: 44px;
          z-index: 151;
          text-transform: none;
          letter-spacing: 0.02em;
        }

        .mobile-library-button:active {
          transform: scale(0.95) translateY(1px);
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.4) 0%, 
            rgba(255, 255, 255, 0.3) 100%);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25),
                      inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }

        .begin-adventure-button {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1.25rem 2.5rem;
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.85) 0%, 
            rgba(255, 255, 255, 0.75) 100%); /* More transparent */
          border: none;
          border-radius: 60px;
          color: #667eea;
          font-size: 1.125rem;
          font-weight: 800;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 12px 30px rgba(255, 255, 255, 0.25),
                      inset 0 1px 0 rgba(255, 255, 255, 0.8); /* Softer shadow */
          min-width: 180px;
          min-height: 80px;
          animation: buttonPulse 2s ease-in-out infinite;
          backdrop-filter: blur(10px); /* Glass effect on button */
          -webkit-backdrop-filter: blur(10px);
        }

        .begin-adventure-button:active {
          transform: scale(0.95);
          box-shadow: 0 15px 40px rgba(255, 255, 255, 0.35),
                      inset 0 2px 0 rgba(255, 255, 255, 1);
        }

        .begin-text {
          font-size: 1rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          line-height: 1;
        }

        @keyframes overlayFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes buttonPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 20px 50px rgba(255, 255, 255, 0.4),
                        inset 0 2px 0 rgba(255, 255, 255, 1);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 25px 60px rgba(255, 255, 255, 0.5),
                        inset 0 2px 0 rgba(255, 255, 255, 1);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .begin-adventure-overlay,
          .begin-adventure-button {
            animation: none !important;
          }
          .begin-adventure-button {
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
}

